import { useEffect } from "react"
import { batch, useSignal } from "@preact/signals-react"
import { postSubmit } from "@ui-seeker/utils"

import { customerSelectedID, isSystemBusy } from "@signals"
import { MODE_BY_SETUP, PRICING_CONFIG_APIS, PRICING_CONFIG_MODES } from "../@constants"
import {
    customerSetup,
    mainSitesData,
    modeChangeAcknowledged,
    pricingConfigMode,
    reloadCustomerSetup,
    rrspData
} from "../@signals"


/** Load what the customer's reference price is today, the moment a customer is picked.
 *
 * No Search button in between: selecting the customer is the whole input of this form, so it fires
 * on change. And the mode selector is **preset from the answer** (`DIPI_USER.role`) instead of
 * defaulting to one branch — the admin should see what the customer *is*, not what we guessed.
 *
 * It also re-runs after saving a role change (`reloadCustomerSetup`): the roles moved, so the
 * summary and the preselected mode would otherwise keep showing the state from before the save.
 */
export default function usePricingConfigSetup() {

    const isLoading = useSignal(false)

    const loadSetup = () => {
        const customerID = customerSelectedID.value
        if (!customerID) {
            batch(() => {
                customerSetup.value = null
                mainSitesData.value = []
                rrspData.value = []
            })
            return
        }

        isLoading.value = true
        postSubmit(PRICING_CONFIG_APIS.SETUP, { customerID })
            .then(res => batch(() => {
                customerSetup.value = res
                pricingConfigMode.value = MODE_BY_SETUP[res?.mode] ?? PRICING_CONFIG_MODES.RRSP
                isLoading.value = false
            }))
            .catch(() => batch(() => {
                customerSetup.value = null
                isLoading.value = false
                isSystemBusy.value = true
            }))
    }

    useEffect(() => {
        // Changing customer drops the previous rows: keeping them would show one customer's config
        // under another one's name while the request is in flight.
        batch(() => {
            mainSitesData.value = []
            rrspData.value = []
            modeChangeAcknowledged.value = false
        })
        loadSetup()
    }, [customerSelectedID.value])

    useEffect(() => {
        if (reloadCustomerSetup.value) {
            loadSetup()
            reloadCustomerSetup.value = false
        }
    }, [reloadCustomerSetup.value])

    return {
        isLoading: isLoading.value
    }
}
